import { useState } from 'react';
import { Download, FileSpreadsheet, Check, Loader2, FileJson } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { PageLayout } from '@/components/Layout/PageLayout';

export default function DataExport() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState<'csv' | 'json' | null>(null);
  const [exportSuccess, setExportSuccess] = useState<'csv' | 'json' | null>(null);

  const exportData = async (format: 'json' | 'csv') => {
    setIsExporting(format);
    setExportSuccess(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const [expenses, income, progress, badges, reflections, budgetSettings, gamificationSettings, recurringExpenses] = await Promise.all([
        supabase.from('expenses').select('*').eq('user_id', user.id),
        supabase.from('income').select('*').eq('user_id', user.id),
        supabase.from('user_progress').select('*').eq('user_id', user.id).single(),
        supabase.from('user_badges').select('*').eq('user_id', user.id),
        supabase.from('weekly_reflections').select('*').eq('user_id', user.id),
        supabase.from('budget_settings').select('*').eq('user_id', user.id).single(),
        supabase.from('gamification_settings').select('*').eq('user_id', user.id).single(),
        supabase.from('recurring_expenses').select('*').eq('user_id', user.id),
      ]);

      const exportPayload = {
        export_date: new Date().toISOString(),
        user_id: user.id,
        expenses: expenses.data || [],
        income: income.data || [],
        recurring_expenses: recurringExpenses.data || [],
        progress: progress.data || {},
        badges: badges.data || [],
        weekly_reflections: reflections.data || [],
        budget_settings: budgetSettings.data || {},
        gamification_settings: gamificationSettings.data || {},
      };

      if (format === 'json') {
        const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { 
          type: 'application/json' 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wren-data-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // CSV export - expenses only (most common use case)
        const csvRows = [
          ['Date', 'Amount', 'Currency', 'Merchant', 'Category', 'Tag', 'Note'],
          ...exportPayload.expenses.map((e: any) => [
            e.expense_date,
            e.amount,
            e.currency,
            (e.merchant || '').replace(/,/g, ';'),
            e.category || '',
            e.tag || '',
            (e.note || '').replace(/,/g, ';').replace(/\n/g, ' ')
          ])
        ];
        const csv = csvRows.map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wren-expenses-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }

      setExportSuccess(format);
      toast({
        title: t('dataExport.exportSuccess'),
        description: t('dataExport.exportSuccessDescription', { format: format.toUpperCase() }),
      });
      
      // Reset success state after 3 seconds
      setTimeout(() => setExportSuccess(null), 3000);
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: t('dataExport.exportFailed'),
        description: t('dataExport.exportFailedDescription'),
        variant: "destructive",
      });
    } finally {
      setIsExporting(null);
    }
  };

  return (
    <PageLayout title={t('dataExport.title')} maxWidth="2xl">
      <div className="space-y-6">
        {/* Main Export Card */}
        <Card className="p-6 space-y-6">
          <div className="flex items-start gap-3">
            <Download className="w-6 h-6 text-primary shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xl font-semibold mb-2">{t('dataExport.instantExport')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('dataExport.instantExportDescription')}
              </p>
            </div>
          </div>

          <div className="grid gap-3">
            {/* CSV Export - Primary action */}
            <Button
              onClick={() => exportData('csv')}
              disabled={isExporting !== null}
              className="w-full min-h-touch justify-between text-left"
              size="lg"
            >
              <div className="flex items-center gap-3">
                {isExporting === 'csv' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : exportSuccess === 'csv' ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <FileSpreadsheet className="h-5 w-5" />
                )}
                <div>
                  <div className="font-medium">{t('dataExport.downloadCSV')}</div>
                  <div className="text-xs opacity-80">{t('dataExport.csvDescription')}</div>
                </div>
              </div>
            </Button>

            {/* JSON Export - Secondary */}
            <Button
              onClick={() => exportData('json')}
              disabled={isExporting !== null}
              variant="outline"
              className="w-full min-h-touch justify-between text-left"
              size="lg"
            >
              <div className="flex items-center gap-3">
                {isExporting === 'json' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : exportSuccess === 'json' ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <FileJson className="h-5 w-5" />
                )}
                <div>
                  <div className="font-medium">{t('dataExport.downloadJSON')}</div>
                  <div className="text-xs text-muted-foreground">{t('dataExport.jsonDescription')}</div>
                </div>
              </div>
            </Button>
          </div>
        </Card>

        {/* What's included */}
        <Card className="p-5">
          <h3 className="font-semibold mb-3">{t('dataExport.whatsIncluded')}</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary shrink-0" />
              {t('dataExport.includesExpenses')}
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary shrink-0" />
              {t('dataExport.includesIncome')}
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary shrink-0" />
              {t('dataExport.includesRecurring')}
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary shrink-0" />
              {t('dataExport.includesProgress')}
            </li>
          </ul>
          <p className="text-xs text-muted-foreground mt-4 pt-3 border-t">
            {t('dataExport.gdprNote')}{' '}
            <a href="mailto:support@trywren.app" className="text-primary underline">
              support@trywren.app
            </a>
          </p>
        </Card>
      </div>
    </PageLayout>
  );
}
